import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatDetail, fmtEUR, fmtDate, fmtBytes } from "@/lib/api";
import { PageHeader, PrimaryButton, EmptyState, StatusBadge } from "@/components/ui-parts";
import { Upload, Loader2, Trash2, Receipt as ReceiptIcon, ExternalLink, CheckCircle2, XCircle, ScanLine, X, FolderInput, Tag, Link2Off } from "lucide-react";
import { toast } from "sonner";
import imageCompression from "browser-image-compression";
import { MonthYearPicker, currentMonthPrefix, isoStartsWithMonth } from "@/components/MonthYearPicker";

export default function Receipts() {
  const [companies, setCompanies] = useState([]);
  const [items, setItems] = useState([]);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterUnmatched, setFilterUnmatched] = useState(false);
  const [filterPayment, setFilterPayment] = useState(""); // '' | 'cash' | 'card' | 'unknown'
  const [monthFilter, setMonthFilter] = useState(currentMonthPrefix());
  const [uploadCompany, setUploadCompany] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'assign' | 'category' | null
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      if (filterUnmatched) params.unmatched_only = true;
      if (filterPayment) params.payment_method = filterPayment;
      const { data } = await api.get("/receipts", { params });
      setItems(data);
    } catch { toast.error("Belege konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/companies");
      setCompanies(data);
    })();
  }, []);
  useEffect(() => { load(); }, [filterCompany, filterUnmatched, filterPayment]);

  // Client-side month filter — server-side would need a schema change and is
  // overkill for the current data volumes. Falls back to created_at when a
  // receipt has no invoice date (e.g. still-extracting entries).
  const filteredItems = React.useMemo(() => {
    if (!monthFilter) return items;
    return items.filter((r) =>
      isoStartsWithMonth(r.date, monthFilter)
      || (!r.date && isoStartsWithMonth(r.created_at, monthFilter))
    );
  }, [items, monthFilter]);

  const compressIfNeeded = async (file) => {
    if (!file.type.startsWith("image/")) return file;
    if (file.size < 3 * 1024 * 1024) return file;
    try {
      return await imageCompression(file, {
        maxSizeMB: 3,
        maxWidthOrHeight: 2600,
        useWebWorker: true,
        initialQuality: 0.85,
      });
    } catch (e) {
      console.warn("compression failed", e);
      return file;
    }
  };

  const uploadOne = async (queueItem) => {
    setUploadQueue((q) => q.map((x) => x.id === queueItem.id ? { ...x, status: "uploading", progress: 5 } : x));
    try {
      const compressed = await compressIfNeeded(queueItem.file);
      const fd = new FormData();
      fd.append("file", compressed, queueItem.file.name);
      if (uploadCompany) fd.append("company_id", uploadCompany);
      const { data } = await api.post("/receipts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (pe) => {
          if (pe.total) {
            const p = Math.min(90, Math.round((pe.loaded / pe.total) * 90));
            setUploadQueue((q) => q.map((x) => x.id === queueItem.id ? { ...x, progress: p } : x));
          }
        },
      });
      setUploadQueue((q) => q.map((x) => x.id === queueItem.id ? { ...x, status: "done", progress: 100, result: data } : x));
    } catch (err) {
      setUploadQueue((q) => q.map((x) => x.id === queueItem.id ? { ...x, status: "error", error: formatDetail(err.response?.data?.detail) || err.message } : x));
    }
  };

  const enqueueFiles = async (files) => {
    if (!files || files.length === 0) return;
    const newItems = Array.from(files).map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      name: f.name,
      size: f.size,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      status: "queued",
      progress: 0,
    }));
    setUploadQueue((q) => [...q, ...newItems]);
    for (const item of newItems) {
      await uploadOne(item);
    }
    load();
  };

  const clearQueue = () => {
    uploadQueue.forEach((x) => x.preview && URL.revokeObjectURL(x.preview));
    setUploadQueue([]);
  };

  const remove = async (r) => {
    if (!window.confirm(`Beleg löschen?`)) return;
    try {
      await api.delete(`/receipts/${r.id}`);
      toast.success("Beleg gelöscht");
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((s) => s.size === filteredItems.length ? new Set() : new Set(filteredItems.map((r) => r.id)));
  };
  const clearSelection = () => setSelected(new Set());

  useEffect(() => { clearSelection(); }, [filterCompany, filterUnmatched, filterPayment]);

  const runBulk = async (payload, successMsg) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/receipts/bulk", { ...payload, receipt_ids: Array.from(selected) });
      toast.success(successMsg(data));
      clearSelection();
      setBulkAction(null);
      setBulkCompany(""); setBulkCategory("");
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`${selected.size} Belege endgültig löschen?`)) return;
    await runBulk({ action: "delete" }, (d) => `${d.count} Belege gelöscht`);
  };

  const bulkAssign = async () => {
    if (!bulkCompany) { toast.error("Firma wählen"); return; }
    await runBulk({ action: "assign_company", company_id: bulkCompany },
      (d) => `${d.count} Belege zugewiesen · ${d.matched || 0} automatisch verknüpft`);
  };

  const bulkSetCategory = async () => {
    await runBulk({ action: "set_category", category: bulkCategory },
      (d) => `${d.count} Belege kategorisiert`);
  };

  const bulkUnmatch = async () => {
    if (!window.confirm(`Zuordnung für ${selected.size} Belege aufheben?`)) return;
    await runBulk({ action: "unmatch" }, (d) => `${d.count} Zuordnungen aufgehoben`);
  };

  const changeCompany = async (r, newCompanyId) => {
    try {
      await api.patch(`/receipts/${r.id}`, { company_id: newCompanyId });
      toast.success("Firma aktualisiert");
      load();
    } catch { toast.error("Fehlgeschlagen"); }
  };

  const changeCategory = async (r, newCategory) => {
    try {
      await api.patch(`/receipts/${r.id}`, { category: newCategory || null });
      toast.success(newCategory ? `Kategorie: ${newCategory}` : "Kategorie entfernt");
      load();
    } catch { toast.error("Kategorie konnte nicht gespeichert werden"); }
  };

  // Collect distinct categories currently in use for autocomplete suggestions.
  const categoryOptions = React.useMemo(() => {
    const set = new Set();
    items.forEach((r) => { if (r.category) set.add(r.category); });
    // Seed with typical categories of the currently-active companies so a
    // new workspace has meaningful suggestions even before any receipt has
    // been categorized.
    companies.forEach((c) => (c.typical_categories || []).forEach((cat) => set.add(cat)));
    return Array.from(set).sort();
  }, [items, companies]);

  const confirmPaymentMethod = async (r, method) => {
    try {
      await api.patch(`/receipts/${r.id}/payment-method`, { payment_method: method });
      toast.success(method === "cash" ? "Als Barzahlung gespeichert" : "Als Kartenzahlung – Bank-Abgleich läuft");
      load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Fehler");
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <PageHeader
        title="Belege"
        subtitle="Rechnungen und Belege hochladen – Fotos werden automatisch zugeschnitten und in PDF umgewandelt."
        actions={<MonthYearPicker value={monthFilter} onChange={setMonthFilter} testid="receipts-month" />}
      />

      <div className="px-8 py-8 space-y-6">
        {companies.length === 0 ? (
          <EmptyState
            title="Zuerst eine Firma anlegen"
            hint="Legen Sie eine Firma an, damit Belege zugeordnet werden können."
            action={<Link to="/app/firmen"><PrimaryButton>Firma anlegen</PrimaryButton></Link>}
          />
        ) : (
          <>
            <div className="bg-white border border-[#E6E6E4] rounded-md p-6">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-1.5">Firma (optional – sonst automatische Zuordnung)</label>
                  <select data-testid="receipt-upload-company"
                    value={uploadCompany} onChange={(e) => setUploadCompany(e.target.value)}
                    className="w-full sm:w-72 px-3 py-2 border border-[#E6E6E4] rounded-md text-sm focus:outline-none focus:border-[#18322B] focus:ring-1 focus:ring-[#18322B]">
                    <option value="">Automatisch erkennen</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); enqueueFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-[#A0A0A0] bg-[#F9F9F8] rounded-lg p-10 flex flex-col items-center justify-center cursor-pointer transition-colors duration-150 hover:border-[#18322B] hover:bg-[#E4F0E8]"
                data-testid="receipt-dropzone">
                <Upload className="h-8 w-8 text-[#5C5E60]" strokeWidth={1.5} />
                <div className="mt-3 text-sm text-[#1A1A1A] font-medium">Fotos, PDFs oder Rechnungen hierher ziehen oder klicken</div>
                <div className="text-xs text-[#5C5E60] mt-1">Beliebig viele Dateien · Große Fotos werden automatisch komprimiert</div>
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
                  className="hidden" onChange={(e) => enqueueFiles(e.target.files)}
                  data-testid="receipt-file-input" />
              </div>

              {uploadQueue.length > 0 && (
                <div className="mt-4 border border-[#E6E6E4] rounded-md overflow-hidden" data-testid="upload-queue">
                  <div className="flex items-center justify-between px-4 py-2 bg-[#F9F9F8] border-b border-[#E6E6E4]">
                    <div className="text-xs font-semibold text-[#1A1A1A]">
                      {uploadQueue.filter((x) => x.status === "done").length} von {uploadQueue.length} verarbeitet
                    </div>
                    <button onClick={clearQueue} className="text-xs text-[#5C5E60] hover:underline">Liste leeren</button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {uploadQueue.map((q) => (
                      <div key={q.id} className="flex items-center gap-3 px-4 py-3 border-b border-[#E6E6E4] last:border-b-0" data-testid={`queue-${q.id}`}>
                        <div className="h-12 w-12 rounded border border-[#E6E6E4] bg-white overflow-hidden shrink-0 flex items-center justify-center">
                          {q.preview ? (
                            <img src={q.preview} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <ReceiptIcon className="h-5 w-5 text-[#5C5E60]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#1A1A1A] truncate">{q.name}</div>
                          <div className="text-xs text-[#5C5E60] flex items-center gap-2">
                            <span>{fmtBytes(q.size)}</span>
                            {q.status === "uploading" && <span>· Hochladen & KI-Analyse ({q.progress}%)</span>}
                            {q.status === "done" && q.result && (
                              <>
                                <span>·</span>
                                {q.result.cropped && (
                                  <span className="inline-flex items-center gap-1 text-[#1E4620]">
                                    <ScanLine className="h-3 w-3" /> automatisch zugeschnitten
                                  </span>
                                )}
                                {q.result.vendor && <span className="truncate">· {q.result.vendor}</span>}
                                {q.result.total_amount && <span>· {fmtEUR(q.result.total_amount)}</span>}
                                {q.result.matched_transaction_id && <span className="text-[#1E4620]">· zugeordnet</span>}
                              </>
                            )}
                            {q.status === "error" && <span className="text-[#B31D1D]">· {q.error}</span>}
                          </div>
                          <div className="mt-1 h-1 rounded-full bg-[#F3F3F1] overflow-hidden">
                            <div className={`h-full transition-all duration-300 ${q.status === "error" ? "bg-[#B31D1D]" : "bg-[#18322B]"}`}
                              style={{ width: `${q.progress}%` }} />
                          </div>
                        </div>
                        <div className="shrink-0">
                          {q.status === "done" && <CheckCircle2 className="h-5 w-5 text-[#1E4620]" strokeWidth={1.75} />}
                          {q.status === "error" && <XCircle className="h-5 w-5 text-[#B31D1D]" strokeWidth={1.75} />}
                          {q.status === "uploading" && <Loader2 className="h-5 w-5 text-[#18322B] animate-spin" />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="receipt-filter-company"
                value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}
                className="px-3 py-2 border border-[#E6E6E4] rounded-md text-sm bg-white focus:outline-none focus:border-[#18322B]">
                <option value="">Alle Firmen</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-[#1A1A1A]">
                <input type="checkbox" checked={filterUnmatched} onChange={(e) => setFilterUnmatched(e.target.checked)}
                  data-testid="receipt-filter-unmatched" />
                Nur nicht zugeordnete
              </label>

              <div className="inline-flex rounded-md border border-[#E6E6E4] overflow-hidden" data-testid="payment-filter">
                {[
                  {v: "", l: "Alle"},
                  {v: "cash", l: "💵 Barzahlungen"},
                  {v: "card", l: "💳 Kartenzahlung"},
                  {v: "unknown", l: "Unklar"},
                ].map((o) => (
                  <button
                    key={o.v || "all"}
                    onClick={() => setFilterPayment(o.v)}
                    data-testid={`payment-filter-${o.v || "all"}`}
                    className={`text-xs px-3 py-2 border-r last:border-r-0 border-[#E6E6E4] transition-colors ${
                      filterPayment === o.v ? "bg-[#18322B] text-white" : "bg-white text-[#1A1A1A] hover:bg-[#F3F3F1]"
                    }`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-sm text-[#5C5E60]">Lade...</div>
            ) : filteredItems.length === 0 ? (
              items.length === 0 ? (
                <EmptyState title="Keine Belege" hint="Laden Sie oben Ihren ersten Beleg hoch." />
              ) : (
                <EmptyState
                  title="Keine Belege in diesem Zeitraum"
                  hint={monthFilter ? 'Wähle einen anderen Monat oder klicke auf „alle" für die gesamte Liste.' : ""}
                />
              )
            ) : (
              <div className="bg-white border border-[#E6E6E4] rounded-md overflow-hidden">
                {selected.size > 0 && (
                  <div className="border-b border-[#E6E6E4] bg-[#E4F0E8] px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
                    <div className="text-sm font-semibold text-[#18322B]">
                      {selected.size} {selected.size === 1 ? "Beleg" : "Belege"} ausgewählt
                    </div>
                    {bulkAction === "assign" ? (
                      <div className="flex items-center gap-2">
                        <select value={bulkCompany} onChange={(e) => setBulkCompany(e.target.value)}
                          data-testid="bulk-company-select"
                          className="text-xs px-2 py-1.5 border border-[#E6E6E4] rounded-md bg-white">
                          <option value="">Firma wählen…</option>
                          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button onClick={bulkAssign} disabled={bulkBusy || !bulkCompany} data-testid="bulk-assign-confirm"
                          className="text-xs px-2.5 py-1.5 rounded-md bg-[#18322B] text-white hover:bg-[#21453B] disabled:opacity-50">
                          {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Zuweisen"}
                        </button>
                        <button onClick={() => { setBulkAction(null); setBulkCompany(""); }} className="text-xs text-[#5C5E60] hover:underline">Abbrechen</button>
                      </div>
                    ) : bulkAction === "category" ? (
                      <div className="flex items-center gap-2">
                        <input value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}
                          placeholder="Kategorie (leer = zurücksetzen)"
                          data-testid="bulk-category-input"
                          className="text-xs px-2 py-1.5 border border-[#E6E6E4] rounded-md w-56" />
                        <button onClick={bulkSetCategory} disabled={bulkBusy} data-testid="bulk-category-confirm"
                          className="text-xs px-2.5 py-1.5 rounded-md bg-[#18322B] text-white hover:bg-[#21453B] disabled:opacity-50">
                          {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Setzen"}
                        </button>
                        <button onClick={() => { setBulkAction(null); setBulkCategory(""); }} className="text-xs text-[#5C5E60] hover:underline">Abbrechen</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setBulkAction("assign")} data-testid="bulk-assign-btn"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-[#18322B]/30 text-[#18322B] hover:bg-white">
                          <FolderInput className="h-3.5 w-3.5" /> Firma zuweisen
                        </button>
                        <button onClick={() => setBulkAction("category")} data-testid="bulk-category-btn"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-[#18322B]/30 text-[#18322B] hover:bg-white">
                          <Tag className="h-3.5 w-3.5" /> Kategorie
                        </button>
                        <button onClick={bulkUnmatch} disabled={bulkBusy} data-testid="bulk-unmatch-btn"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-[#18322B]/30 text-[#18322B] hover:bg-white">
                          <Link2Off className="h-3.5 w-3.5" /> Zuordnung aufheben
                        </button>
                        <button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete-btn"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-[#B31D1D]/30 text-[#B31D1D] hover:bg-[#FCE8E8]">
                          <Trash2 className="h-3.5 w-3.5" /> Löschen
                        </button>
                      </div>
                    )}
                    <div className="flex-1" />
                    <button onClick={clearSelection} className="text-xs text-[#5C5E60] hover:text-[#1A1A1A] inline-flex items-center gap-1" data-testid="bulk-clear">
                      <X className="h-3.5 w-3.5" /> Auswahl leeren
                    </button>
                  </div>
                )}
                <table className="w-full text-sm" data-testid="receipts-list">
                  <datalist id="receipt-category-suggestions">
                    {categoryOptions.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.15em] text-[#5C5E60] bg-[#F9F9F8] border-b border-[#E6E6E4]">
                      <th className="pl-6 pr-2 py-3 w-8">
                        <input type="checkbox" data-testid="select-all"
                          checked={filteredItems.length > 0 && selected.size === filteredItems.length}
                          onChange={toggleSelectAll} />
                      </th>
                      <th className="text-left px-4 py-3 font-semibold">Beleg</th>
                      <th className="text-left px-4 py-3 font-semibold">Datum</th>
                      <th className="text-right px-4 py-3 font-semibold">Betrag</th>
                      <th className="text-left px-4 py-3 font-semibold">Firma</th>
                      <th className="text-left px-4 py-3 font-semibold">Kategorie</th>
                      <th className="text-left px-4 py-3 font-semibold">Status</th>
                      <th className="text-right px-6 py-3 font-semibold">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((r) => (
                      <tr key={r.id}
                        className={`border-b border-[#E6E6E4] last:border-b-0 transition-colors duration-150 ${selected.has(r.id) ? "bg-[#E4F0E8]/40" : "hover:bg-[#F9F9F8]"}`}>
                        <td className="pl-6 pr-2 py-3">
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)}
                            data-testid={`select-receipt-${r.id}`} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ReceiptIcon className="h-4 w-4 text-[#5C5E60]" strokeWidth={1.75} />
                            <div>
                              <div className="font-medium text-[#1A1A1A] flex items-center gap-2">
                                {r.vendor || r.filename}
                                {r.source === "telegram" && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#EAF4FA] text-[#1A4971]">Telegram</span>}
                                {r.cropped && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[#E4F0E8] text-[#18322B]">Auto-Crop</span>}
                              </div>
                              <div className="text-xs text-[#5C5E60]">{r.invoice_number ? `Nr. ${r.invoice_number}` : r.filename}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono-tab text-[#1A1A1A]">{fmtDate(r.date)}</td>
                        <td className="text-right px-4 py-3 font-mono-tab font-semibold text-[#1A1A1A]">{fmtEUR(r.total_amount, r.currency)}</td>
                        <td className="px-4 py-3">
                          <select data-testid={`receipt-company-${r.id}`}
                            value={r.company_id || ""}
                            onChange={(e) => changeCompany(r, e.target.value)}
                            className="text-xs px-2 py-1 border border-[#E6E6E4] rounded-md bg-white hover:border-[#18322B]">
                            <option value="">— Keine —</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            data-testid={`receipt-category-${r.id}`}
                            defaultValue={r.category || ""}
                            list="receipt-category-suggestions"
                            placeholder="—"
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if (v !== (r.category || "")) changeCategory(r, v);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                            className="text-xs px-2 py-1 border border-[#E6E6E4] rounded-md bg-white hover:border-[#18322B] w-36" />
                        </td>
                        <td className="px-4 py-3">
                          {r.matched_transaction_id
                            ? <StatusBadge variant="success">Zugeordnet</StatusBadge>
                            : r.payment_method === "cash" && r.cash_confirmed
                              ? <StatusBadge variant="warning">💵 Barzahlung</StatusBadge>
                              : r.payment_method === "cash" && !r.cash_confirmed
                                ? <StatusBadge variant="warning">💵 Bar? – bestätigen</StatusBadge>
                                : <StatusBadge variant="warning">Offen</StatusBadge>}
                        </td>
                        <td className="text-right px-6 py-3">
                          <a href={`${backendUrl}/api/files/${r.storage_path}`} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#1A1A1A] mr-1 transition-colors duration-150"
                            data-testid={`view-receipt-${r.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> PDF
                          </a>
                          {r.original_path && (
                            <a href={`${backendUrl}/api/files/${r.original_path}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#5C5E60] mr-1 transition-colors duration-150"
                              data-testid={`view-original-${r.id}`}
                              title="Original-Foto">
                              Original
                            </a>
                          )}
                          {r.payment_method === "cash" && !r.cash_confirmed && (
                            <>
                              <button onClick={() => confirmPaymentMethod(r, "cash")} data-testid={`confirm-cash-${r.id}`}
                                title="Als Barzahlung bestätigen"
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-[#FFF4E5] hover:bg-[#FFE4B8] text-[#8B4A0E] mr-1 transition-colors duration-150 font-semibold">
                                ✅ Bar
                              </button>
                              <button onClick={() => confirmPaymentMethod(r, "card")} data-testid={`confirm-card-${r.id}`}
                                title="War doch Karte"
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#F3F3F1] text-[#5C5E60] mr-1 transition-colors duration-150">
                                💳 Karte
                              </button>
                            </>
                          )}
                          <button onClick={() => remove(r)} data-testid={`delete-receipt-${r.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md hover:bg-[#FCE8E8] text-[#B31D1D] transition-colors duration-150">
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
